// src/features/branch/components/branch-table.tsx
import React from "react";
import { BranchStatusCode } from "../services/branch-service";
import EmptyState from "../../../components/custom/empty-state";
import { useViewportMaxHeight } from "../../../hooks/useViewportMaxHeight";

export interface BranchRow {
    id: number;
    company_id: number;
    name: string;
    phone: string;
    description: string;
    address: string;
    user_count: number;
    statusCode: BranchStatusCode;
    /** 1 = nhà máy giặt — hiện nhãn cạnh tên để phân biệt với cửa hàng */
    isFactory?: boolean;
    created_at: string;
    updated_at: string;
    latitude?: number | null;
    longitude?: number | null;
    attendanceRadiusM?: number | null;
  }
  

interface BranchTableProps {
  rows: BranchRow[];
  isLoading: boolean;
  startIndex: number;
  onEdit?: (row: BranchRow) => void;
  onDeactivate?: (row: BranchRow) => void;
  onActivate?: (row: BranchRow) => void;
  onViewDetail?: (row: BranchRow) => void;
}

const BranchTable: React.FC<BranchTableProps> = ({
  rows,
  isLoading,
  startIndex,
  onEdit,
  onDeactivate,
  onActivate,
  onViewDetail,
}) => {
  // Khung bảng cao vừa tới đáy viewport; nhiều dòng hơn thì cuộn bên trong khung, tiêu đề cột đứng yên.
  const { ref: frameRef, maxHeight } = useViewportMaxHeight<HTMLDivElement>();
  const frame = (content: React.ReactNode) => (
    <div ref={frameRef} style={{ maxHeight }} className="overflow-auto rounded-lg border border-gray-200 bg-white">
      {content}
    </div>
  );

  if (isLoading) {
    return frame(<div className="p-4 text-gray-500">Đang tải...</div>);
  }

  if (!rows.length) {
    return frame(<EmptyState message="Chưa có chi nhánh" description="Không tìm thấy chi nhánh khớp bộ lọc." />);
  }

  // border-separate: với border-collapse, viền của ô tiêu đề sticky bị cuộn mất theo nội dung.
  return frame(
    <table className="min-w-full border-separate border-spacing-0 text-sm [&_tr>*:last-child]:border-r-0 [&_tbody>tr:last-child>td]:border-b-0">
      <thead className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-gray-100">
        <tr>
          <th className="border-b border-r border-gray-200 px-3 py-2 text-center">STT</th>
          <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Tên chi nhánh</th>
          <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Số điện thoại</th>
          <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Địa chỉ</th>
          <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Mô tả</th>
          <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Số người dùng</th>
          <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Hành động</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={row.id} className="hover:bg-gray-50">
            <td className="border-b border-r border-gray-200 px-3 py-2 text-center">{startIndex + idx}</td>
            <td className="border-b border-r border-gray-200 px-3 py-2">
              {row.name}
              {row.isFactory && (
                <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                  Nhà máy
                </span>
              )}
            </td>
            <td className="border-b border-r border-gray-200 px-3 py-2">{row.phone}</td>
            <td className="border-b border-r border-gray-200 px-3 py-2">{row.address ?? "-"}</td>
            <td className="border-b border-r border-gray-200 px-3 py-2">{row.description}</td>
            <td className="border-b border-r border-gray-200 px-3 py-2 text-center">{row.user_count}</td>
            <td className="border-b border-r border-gray-200 px-3 py-2">
                <div className="flex justify-center gap-2">
                    {onViewDetail && (
                    <button
                        type="button"
                        onClick={() => onViewDetail(row)}
                        aria-label="Xem chi tiết"
                        className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-slate-500 text-white shadow-sm transition hover:bg-slate-600"
                    >
                        {/* Icon con mắt */}
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                        className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51
                            7.36 4.5 12 4.5c4.638 0 8.573 3.007
                            9.963 7.178.07.207.07.431 0
                            .639C20.577 16.49 16.64 19.5 12
                            19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="pointer-events-none absolute bottom-11 left-1/2 z-20 hidden
                        -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1
                        text-xs font-medium text-white group-hover/action:block">
                        Xem
                        </span>
                    </button>
                    )}

                    {onEdit && (
                    <button
                        type="button"
                        onClick={() => onEdit(row)}
                        aria-label="Sửa"
                        className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-yellow-500 text-white shadow-sm transition hover:bg-yellow-600"
                    >
                        {/* Icon bút ✏ */}
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                        className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round"
                            d="M16.862 4.487l1.687-1.688a1.875
                            1.875 0 112.652 2.652l-9.547
                            9.546a4.5 4.5 0 01-1.897 1.13l-3.034
                            .909.91-3.034a4.5 4.5 0 011.129-1.897l9.547-9.546z" />
                        </svg>
                        <span className="pointer-events-none absolute bottom-11 left-1/2 z-20 hidden
                        -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1
                        text-xs font-medium text-white group-hover/action:block">
                        Sửa
                        </span>
                    </button>
                    )}

                    {row.statusCode === BranchStatusCode.ACTIVE && onDeactivate && (
                    <button
                        type="button"
                        onClick={() => onDeactivate(row)}
                        aria-label="Ngưng"
                        className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-red-500 text-white shadow-sm transition hover:bg-red-600"
                    >
                        {/* Icon dừng ⛔ */}
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                        className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round"
                            d="M9.75 9.75h4.5v4.5h-4.5z" />
                        <path strokeLinecap="round" strokeLinejoin="round"
                            d="M12 21.75c5.385 0 9.75-4.365
                            9.75-9.75S17.385 2.25 12 2.25
                            2.25 6.615 2.25 12 6.615 21.75
                            12 21.75z" />
                        </svg>
                        <span className="pointer-events-none absolute bottom-11 left-1/2 z-20 hidden
                        -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1
                        text-xs font-medium text-white group-hover/action:block">
                        Ngưng
                        </span>
                    </button>
                    )}

                    {row.statusCode === BranchStatusCode.INACTIVE && onActivate && (
                    <button
                        type="button"
                        onClick={() => onActivate(row)}
                        aria-label="Kích hoạt"
                        className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-green-500 text-white shadow-sm transition hover:bg-green-600"
                    >
                        {/* Icon check ✅ */}
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                        className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round"
                            d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <span className="pointer-events-none absolute bottom-11 left-1/2 z-20 hidden
                        -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1
                        text-xs font-medium text-white group-hover/action:block">
                        Kích hoạt
                        </span>
                    </button>
                    )}
                </div>
                </td>

          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default BranchTable;
